import CloseIcon from '@mui/icons-material/Close'
import SearchIcon from '@mui/icons-material/Search'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'

import LocalStorageButtons from '@components/LocalStorageButtons'
import FilterFromToDate from '@components/toolbar/FilterFromToDate'
import FilterGridContainer from '@components/toolbar/FilterGridContainer'
import FilterGridItem from '@components/toolbar/FilterGridItem'
import { FilterMyList } from '@components/toolbar/FilterMyList'
import { FilterSelectGebied } from '@components/toolbar/FilterSelectGebied'
import type { FilterOption, GroupedFilterOption } from '@models/Filter'
import type { Gebied } from '@models/Gebied'

import { useAuthenticatedUser } from '../../../../auth/hooks/useAuthenticatedUser'
import { SelectObject } from '../../../../core/components/SelectObject'
import { useLocalFilters } from '../../../../core/hooks/useLocalFilters'
import { queryKey } from '../../hooks/useWerklijsten'
import type { FetchWerklijstenFilters } from '../../types/FetchWerklijsten'

interface Props {
  filters: FetchWerklijstenFilters
  initialFilters: FetchWerklijstenFilters
  onChange: (filters: FetchWerklijstenFilters) => void
  onRefresh: () => void
  onReset: () => void
  filterOptions: {
    gebieden: Gebied[]
    actiehouders: GroupedFilterOption[]
    labels: FilterOption[]
  }
}

export const WerklijstenFilters = ({
  filters,
  initialFilters,
  onChange,
  onRefresh,
  onReset,
  filterOptions: { gebieden, actiehouders, labels },
}: Props) => {
  const { localFilters, handleFilterChange, handleZoeken, handleReset } =
    useLocalFilters({
      filters,
      initialFilters,
      onChange: (filters) => onChange(filters),
      onRefresh,
      onReset,
    })
  const { combi } = useAuthenticatedUser()

  return (
    <FilterGridContainer>
      <FilterGridItem>
        <FilterSelectGebied
          options={gebieden}
          value={localFilters.gebied}
          onFilterChange={handleFilterChange}
        />
      </FilterGridItem>

      {combi && (
        <FilterGridItem>
          <SelectObject
            options={actiehouders}
            label="Organisatie"
            value={localFilters.organisatie}
            onChange={(value) => handleFilterChange({ organisatie: value })}
          />
        </FilterGridItem>
      )}

      <FilterGridItem minColumns="double">
        <FilterFromToDate
          fromDate={localFilters.fromGeplandeUitvoeringsdatum}
          toDate={localFilters.toGeplandeUitvoeringsdatum}
          onFilterChange={handleFilterChange}
          label="Project gepland"
          prop="GeplandeUitvoeringsdatum"
        />
      </FilterGridItem>

      {labels.length > 0 && (
        <FilterGridItem>
          <SelectObject
            multiple
            options={labels}
            label="Labels"
            value={localFilters.labels}
            onChange={(value) => handleFilterChange({ labels: value })}
          />
        </FilterGridItem>
      )}

      <FilterGridItem
        minColumns="full"
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <Tooltip title="Reset filters">
          <IconButton onClick={() => handleReset()}>
            <CloseIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title="Zoeken">
          <IconButton onClick={() => handleZoeken()}>
            <SearchIcon />
          </IconButton>
        </Tooltip>

        <FilterMyList
          myList={localFilters.myList}
          onFilterChange={handleFilterChange}
        />

        <LocalStorageButtons
          name={queryKey}
          storable={localFilters}
          onClickLoad={handleFilterChange}
        />
      </FilterGridItem>
    </FilterGridContainer>
  )
}
